"use client";

import "./fullcalendar.css";

import { useEffect, useState } from "react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import listPlugin from "@fullcalendar/list";
import interactionPlugin from "@fullcalendar/interaction";
import luxonPlugin from "@fullcalendar/luxon3";
import { formatInTimeZone } from "date-fns-tz";
import type { EventContentArg, EventClickArg } from "@fullcalendar/core";

/** Mismo umbral que el resto de la agenda (Panda `sm` = 640px). */
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 640);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return isMobile;
}

export function CalendarView({
  view,
  initialDate,
  events,
  timeZone,
  onDates,
  onEventClick,
  onSelect,
  onEventDrop,
  onEventResize,
  eventContent,
  calendarRef,
  onMoreLinkClick,
}: {
  view: "timeGridDay" | "timeGridWeek" | "dayGridMonth" | "listWeek";
  initialDate: Date;
  events: any[];
  /** Zona horaria IANA del profesional. FullCalendar renderiza e interpreta horas en esta zona. */
  timeZone: string;
  onDates: (range: { start: Date; end: Date }) => void;
  onEventClick: (arg: EventClickArg) => void;
  onSelect: (arg: any) => void;
  onEventDrop: (arg: any) => void;
  onEventResize: (arg: any) => void;
  eventContent: (arg: EventContentArg) => any;
  calendarRef: React.RefObject<FullCalendar>;
  onMoreLinkClick?: (date: Date, allEvents: any[]) => void;
}) {
  const isMobile = useIsMobile();

  return (
    <div
      style={
        isMobile
          ? { width: "100%", minWidth: 0 }
          : { height: "calc(100dvh - 220px)", minHeight: "400px", width: "100%", minWidth: 0 }
      }
    >
      <FullCalendar
        ref={calendarRef}
        plugins={[dayGridPlugin, timeGridPlugin, listPlugin, interactionPlugin, luxonPlugin]}
        timeZone={timeZone}
        initialView={view}
        initialDate={initialDate}
        headerToolbar={false}
        locale="es"
        slotMinTime="07:00:00"
        slotMaxTime="22:00:00"
        slotDuration="00:30:00"
        snapDuration="00:15:00"
        slotEventOverlap={false}
        eventMinHeight={60}
        allDaySlot={true}
        editable
        selectable
        selectMirror
        eventResizableFromStart={false}
        nowIndicator
        dayMaxEventRows={4}
        businessHours={{
          startTime: "08:00",
          endTime: "20:00",
          daysOfWeek: [1, 2, 3, 4, 5, 6],
        }}
        /* En mobile "auto" hace que FullCalendar crezca con su contenido y no
           arme su propio scroller interno: un solo scroll, el de la página. */
        height={isMobile ? "auto" : "100%"}
        noEventsContent="No tenés turnos en este período"
        events={events}
        datesSet={(arg) => onDates({ start: arg.start, end: arg.end })}
        eventClick={onEventClick}
        select={onSelect}
        eventDrop={onEventDrop}
        eventResize={onEventResize}
        eventContent={eventContent}
        moreLinkClick={(info) => {
          // Derivamos el día calendario desde el instante real del primer evento, formateado en la
          // zona del profesional. Evita la aritmética de offset del navegador (que rompía con DST /
          // navegador en otra zona). El "ver más" siempre tiene al menos un evento.
          const segs = info.allSegs.map((s: any) => s.event);
          const firstStart: Date | null = segs[0]?.start ?? null;
          let calendarDay = info.date;
          if (firstStart) {
            const ymd = formatInTimeZone(firstStart, timeZone, "yyyy-MM-dd");
            const [y, mo, d] = ymd.split("-").map(Number);
            calendarDay = new Date(y, mo - 1, d); // medianoche local solo para el título del modal
          }
          onMoreLinkClick?.(calendarDay, segs);
          // Returning a truthy non-string prevents FC from opening its built-in popover.
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          return {} as any;
        }}
        moreLinkContent={() => "ver más"}
      />
    </div>
  );
}

